import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Heart, CreditCard, Mail, CheckCircle, Loader2, AlertCircle,
  ArrowRight, RefreshCw, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SectionHeading from '../components/SectionHeading';
import LaserBorder from '../components/LaserBorder';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const donationAmounts = [
  { key: '10', value: 10, label: '$10' },
  { key: '25', value: 25, label: '$25' },
  { key: '50', value: 50, label: '$50' },
  { key: '100', value: 100, label: '$100' },
  { key: '250', value: 250, label: '$250' },
  { key: '500', value: 500, label: '$500' },
  { key: 'custom', value: null, label: 'Custom' },
];

const categories = [
  { id: 'place-to-worship', name: '🕌 Place to Worship Campaign', icon: Heart },
  { id: 'orphans', name: 'Orphan Support', icon: Heart },
  { id: 'food', name: 'Food Bank', icon: Heart },
  { id: 'clothing', name: 'Clothing & Winter Kits', icon: Heart },
  { id: 'education', name: 'Education', icon: Heart },
  { id: 'zakatul-fitr', name: 'Zakatul Fitr ($10/person)', icon: Heart },
  { id: 'fundraising', name: '2026 Annual Fundraising', icon: Heart },
  { id: 'general', name: 'General Fund', icon: Heart },
];

const DonatePage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const subscriptionType = searchParams.get('type');
  const categoryParam = searchParams.get('category');
  const amountParam = searchParams.get('amount');
  const recurringParam = searchParams.get('recurring');

  const presetAmounts = ['10', '25', '50', '100', '250', '500'];
  const initialAmount = amountParam && presetAmounts.includes(amountParam) ? amountParam
    : amountParam ? 'custom' : '50';

  const [selectedAmount, setSelectedAmount] = useState(initialAmount);
  const [customAmount, setCustomAmount] = useState(amountParam && !presetAmounts.includes(amountParam) ? amountParam : '');
  const [selectedCategory, setSelectedCategory] = useState(categoryParam || 'fundraising');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [donationType, setDonationType] = useState(recurringParam === '1' ? 'monthly' : 'one-time'); // 'one-time' or 'monthly'

  // Set default amount based on category
  useEffect(() => {
    if (selectedCategory === 'zakatul-fitr') {
      setSelectedAmount('10');
      setCustomAmount('');
    }
  }, [selectedCategory]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (sessionId && !paymentStatus && pollAttempts < 5) {
      const pollStatus = async () => {
        try {
          const response = await axios.get(`${API}/donations/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            setPaymentStatus(subscriptionType === 'subscription' ? 'subscription-success' : 'success');
            toast.success(subscriptionType === 'subscription' 
              ? 'Thank you! Your monthly donation has been set up!' 
              : 'Thank you for your generous donation!');
          } else if (response.data.status === 'expired') {
            setPaymentStatus('failed');
            toast.error('Payment session expired. Please try again.');
          } else {
            // Continue polling
            setPollAttempts(prev => prev + 1);
            setTimeout(pollStatus, 2000);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          // For subscription, if we can't find the session, it might still be successful
          if (subscriptionType === 'subscription') {
            setPaymentStatus('subscription-success');
            toast.success('Thank you! Your monthly donation has been set up!');
          } else {
            setPollAttempts(prev => prev + 1);
          }
        }
      };
      
      pollStatus();
    }
  }, [sessionId, paymentStatus, pollAttempts, subscriptionType]);

  const handleOnlineDonate = async () => {
    const amount = selectedAmount === 'custom' ? parseFloat(customAmount) : donationAmounts.find(a => a.key === selectedAmount)?.value;
    
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);
    try {
      // Use different endpoint for recurring donations
      const endpoint = donationType === 'monthly' 
        ? `${API}/donations/subscribe` 
        : `${API}/donations/checkout`;
      
      const response = await axios.post(endpoint, {
        amount_key: selectedAmount,
        custom_amount: selectedAmount === 'custom' ? amount : null,
        category: selectedCategory,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Something went wrong. Please try again or use e-Transfer.');
      setIsProcessing(false);
    }
  };

  // Show success page if payment completed
  if (paymentStatus === 'success' || paymentStatus === 'subscription-success') {
    const isSubscription = paymentStatus === 'subscription-success';
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center p-8"
          data-testid="donation-success"
        >
          <div className={`w-20 h-20 rounded-full ${isSubscription ? 'bg-primary/20' : 'bg-green-500/20'} flex items-center justify-center mx-auto mb-6`}>
            {isSubscription ? (
              <RefreshCw className="h-10 w-10 text-primary" />
            ) : (
              <CheckCircle className="h-10 w-10 text-green-500" />
            )}
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            {isSubscription ? 'Monthly Donation Set Up!' : 'Thank You!'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {isSubscription 
              ? 'Your recurring monthly donation has been set up. You will be charged automatically each month. May Allah reward you abundantly for your ongoing generosity.'
              : 'Your donation has been received. May Allah reward you abundantly for your generosity.'}
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            {isSubscription
              ? 'You can manage your subscription anytime through the link in your confirmation email.'
              : 'A confirmation will be sent to your email. Tax receipts will be issued once our CRA registration is approved.'}
          </p>
          <Button onClick={() => window.location.href = '/'} className="rounded-full">
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Support Our Mission
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Make a <span className="text-gradient">Donation</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Your generous contribution helps us serve orphans and vulnerable families in our community.
              Every dollar makes a difference.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Section */}
      <section className="py-24 lg:py-32" data-testid="donation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Donation Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Tabs defaultValue="online" className="space-y-8">
                <TabsList className="grid w-full grid-cols-2 bg-card border border-border rounded-xl p-1">
                  <TabsTrigger value="online" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Online Payment
                  </TabsTrigger>
                  <TabsTrigger value="etransfer" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Mail className="mr-2 h-4 w-4" />
                    e-Transfer
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="online" className="space-y-6" data-testid="online-donation-tab">
                  {/* One-time vs Monthly Toggle */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Donation Frequency</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setDonationType('one-time')}
                        className={`p-4 rounded-xl border text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                          donationType === 'one-time'
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                        }`}
                        data-testid="donation-type-onetime"
                      >
                        <Heart className="h-4 w-4" />
                        One-Time
                      </button>
                      <button
                        onClick={() => setDonationType('monthly')}
                        className={`p-4 rounded-xl border text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                          donationType === 'monthly'
                            ? 'bg-secondary/20 border-secondary text-secondary'
                            : 'bg-card border-border text-muted-foreground hover:border-secondary/50'
                        }`}
                        data-testid="donation-type-monthly"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Monthly
                      </button>
                    </div>
                    {donationType === 'monthly' && (
                      <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-secondary flex items-center gap-1"
                      >
                        <Calendar className="h-3 w-3" />
                        You'll be charged automatically each month
                      </motion.p>
                    )}
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">Donation Category</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                            selectedCategory === category.id
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'bg-card border-border text-muted-foreground hover:border-primary/50'
                          }`}
                          data-testid={`category-${category.id}`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Amount Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Select Amount (CAD){donationType === 'monthly' && <span className="text-secondary ml-1">/month</span>}
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {donationAmounts.map((amount) => (
                        <button
                          key={amount.key}
                          onClick={() => setSelectedAmount(amount.key)}
                          className={`p-4 rounded-xl border text-lg font-semibold transition-all ${
                            selectedAmount === amount.key
                              ? donationType === 'monthly' 
                                ? 'bg-secondary/20 border-secondary text-secondary'
                                : 'bg-primary/10 border-primary text-primary'
                              : 'bg-card border-border text-foreground hover:border-primary/50'
                          }`}
                          data-testid={`amount-${amount.key}`}
                        >
                          {amount.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount Input */}
                  {selectedAmount === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Enter Amount (CAD){donationType === 'monthly' && <span className="text-secondary ml-1">/month</span>}
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <input
                          type="number"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full h-12 pl-8 pr-4 rounded-xl bg-card border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground"
                          min="1"
                          step="0.01"
                          data-testid="custom-amount-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* Donate Button */}
                  <Button 
                    onClick={handleOnlineDonate}
                    disabled={isProcessing}
                    className={`w-full h-14 rounded-xl text-lg font-semibold ${
                      donationType === 'monthly'
                        ? 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90 glow-teal'
                    }`}
                    data-testid="donate-online-btn"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : donationType === 'monthly' ? (
                      <>
                        <RefreshCw className="mr-2 h-5 w-5" />
                        Donate ${selectedAmount === 'custom' ? (customAmount || '0') : donationAmounts.find(a => a.key === selectedAmount)?.value}/month
                      </>
                    ) : (
                      <>
                        <Heart className="mr-2 h-5 w-5" />
                        Donate ${selectedAmount === 'custom' ? (customAmount || '0') : donationAmounts.find(a => a.key === selectedAmount)?.value} CAD
                      </>
                    )}
                  </Button>
                  
                  {/* Monthly donation benefit message */}
                  {donationType === 'monthly' && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-secondary/10 border border-secondary/20"
                    >
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-secondary">Why give monthly?</strong> Consistent monthly donations help us plan and sustain our programs for orphans and families in need. You can cancel anytime.
                      </p>
                    </motion.div>
                  )}
                </TabsContent>

                <TabsContent value="etransfer" className="space-y-6" data-testid="etransfer-tab">
                  <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
                    <h3 className="text-lg font-semibold font-['Outfit'] text-foreground">Send e-Transfer to:</h3>
                    <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                      <p className="text-lg font-mono text-primary break-all">
                        truenorthmuslim@gmail.com
                      </p>
                    </div>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong className="text-foreground">In the message, please include:</strong></p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Your full name</li>
                        <li>Your email address</li>
                        <li>Donation category (Orphans, Food Bank, Education, Medical, Shelter, or General)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-secondary/10 border border-secondary/20">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-muted-foreground">
                        No password required for Interac e-Transfer. The funds will be deposited automatically.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>

            {/* Sidebar Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <LaserBorder>
                <div className="p-8 space-y-6">
                  <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Where Your Donation Goes</h3>
                  <div className="space-y-4">
                    {categories.slice(0, 5).map((category) => (
                      <div key={category.id} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-muted-foreground">{category.name}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    100% of your designated donation goes directly to the specified program.
                  </p>
                </div>
              </LaserBorder>

              <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
                <h4 className="font-semibold text-foreground font-['Outfit']">Tax Receipts</h4>
                <p className="text-sm text-muted-foreground">
                  True North Muslim Community is applying for registered charity status with CRA. 
                  Tax receipts will be issued once our registration is approved.
                </p>
                <div className="flex items-center gap-2 text-sm text-secondary">
                  <AlertCircle className="h-4 w-4" />
                  <span>Registered charity status pending</span>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-card border border-border">
                <blockquote className="text-lg italic text-foreground mb-3">
                  "The believer's shade on the Day of Resurrection will be their charity."
                </blockquote>
                <p className="text-sm text-muted-foreground">— Prophet Muhammad (PBUH)</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonatePage;
